// Packages and components
import React, { useCallback, useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDInputDate from '@ui/InputDate/SHDInputDate';
import SHDInputTime from '@ui/InputTime/SHDInputTime';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDSelect from '@ui/Select/SHDSelect';
import SHDText from '@ui/Text/SHDText';
import DeleteGameModal from './DeleteGameModal';

// Helpers and utlities
import { BASEBALL, BASKETBALL, SOCCER, SOFTBALL } from '@constants/strings';
import { calculateTs } from '@helpers/dateUtils';
import { getStringDate, getAdjustedTime } from '@theme/utils/data';
import useGame from '@hooks/useGame';
import { useModal } from '@components/ModalContext';

// Types and enums
import type { CreateNewGameProps } from './schedule.d';
import type { SelectOption } from '@theme/theme.d';

const CreateNewGame: React.FC<CreateNewGameProps> = ({
  game,
  mode = 'create',
  onClose,
  teamId,
  sportType,
}) => {
  // States
  const [time, setTime] = useState<SelectOption>(
    getAdjustedTime(game?.time.split(':')[0], game?.time.split(':')[1])
  );

  const [date, setDate] = useState<string>(game ? game.date : getStringDate());
  const [opponent, setOpponent] = useState<string>(
    game ? game.scoreOpponentName : ''
  );
  const [gameTitle, setGameTitle] = useState<string>(game ? game.title : '');
  const [sanctioningPartner, setSanctioningPartner] = useState<SelectOption>({
    value: game ? game.sanctioningPartner : '',
    label: game ? game.sanctioningPartner : '',
  });

  // Contexts
  const { showModal, hideModal } = useModal();

  // Hooks
  const { createGame, updateGame, deleteGame, loading } = useGame();

  // Handlers
  const handleSanctioningPartnerOptions =
    useCallback((): Array<SelectOption> => {
      let options: Array<SelectOption> = [];
      switch (sportType) {
        case BASKETBALL.display:
          options = [{ value: 'teammate', label: 'Teammate' }];
          break;
        case SOCCER.display:
          options = [{ value: '3v3live', label: '3v3 Live' }];
          break;
        case BASEBALL.display:
          options = [
            { value: 'perfectgame_bb', label: 'Perfect Game' },
            { value: 'little_league_bb', label: 'Little League' },
            { value: 'babe_ruth_bb', label: 'Babe Ruth League / Cal Ripken' },
            { value: 'usssa_bb', label: 'USSSA' },
            { value: 'pony_bb', label: 'PONY' },
            { value: 'american_legion_bb', label: 'American Legion' },
            { value: 'aau_bb', label: 'AAU' },
          ];
          break;
        case SOFTBALL.display:
          options = [
            { value: 'perfectgame_sb', label: 'Perfect Game' },
            { value: 'little_league_sb', label: 'Little League' },
            { value: 'usa_softball_sb', label: 'USA Softball' },
            { value: 'babe_ruth_sb', label: 'Babe Ruth League / Cal Ripken' },
            { value: 'usssa_sb', label: 'USSSA' },
            { value: 'pony_sb', label: 'PONY' },
            { value: 'pgf_bb', label: 'Premier Girls Fastpitch' },
            { value: 'alliance_sb', label: 'Alliance Fastpitch' },
            { value: 'top_gun_sb', label: 'Top Gun' },
            { value: 'top_club_sb', label: 'Top Club' },
            { value: 'triple_crown_sb', label: 'Triple Crown' },
          ];
          break;
        default:
          options.push({ value: 'none', label: 'None' });
          break;
      }
      return options;
    }, [sportType]);

  const handleCreateGame = async () => {
    const scheduleTsStart = calculateTs(date, time?.value as string);
    const gameDetails = {
      date,
      time: time?.value as string,
      scoreOpponentName: opponent || 'TBD Opponent',
      scheduleTsStart,
      teamId: teamId,
      sportType: sportType || game?.sportType || 'baseball',
      title: gameTitle,
      sanctioningPartner: sanctioningPartner.value,
    };

    try {
      await createGame(gameDetails, teamId, sportType).then(() => {
        onClose && onClose(false);
      });
    } catch (err) {
      throw new Error(err as string);
    }
  };

  const handleUpdateGame = async () => {
    try {
      const payload = {
        date: date,
        time: time?.value as string,
        scoreOpponentName: opponent,
        sanctioningPartner: sanctioningPartner.value,
        title: gameTitle,
      };
      await updateGame(teamId, game?.id as string, payload).then(() => {
        onClose && onClose(false);
      });
    } catch (err) {
      throw new Error(err as string);
    }
  };

  const handleShowDeleteGame = () => {
    showModal(
      <DeleteGameModal onClose={hideModal} onDeleteGame={handleDeleteGame} />,
      {
        onClose: () => hideModal(),
        onGoBack: undefined,
        isRelativeHeight: true,
        isConfirmation: true,
      }
    );
  };

  const handleDeleteGame = async () => {
    hideModal();
    try {
      await deleteGame(teamId, game?.id as string).then(() => {
        onClose && onClose(false);
      });
    } catch (err) {
      throw new Error(err as string);
    }
  };

  // Renderers
  const renderTrayTitle = () => {
    return (
      <SHDHeading level={3}>
        {mode === 'create' ? 'Create new game' : 'Edit game'}
      </SHDHeading>
    );
  };

  return (
    <>
      <SHDModalContainer>
        <Box margin={{ bottom: '30px' }}>{renderTrayTitle()}</Box>
        <Box
          data-testid="create-game-tray"
          gap="12px"
          style={{
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box
            direction="row"
            align="center"
            justify="between"
            gap="medium"
            flex="grow"
          >
            <SHDText style="subhead-semibold">Date</SHDText>
            <Box width="170px">
              <SHDInputDate
                onChange={(value) => setDate(value as string)}
                label=""
                showIcon={true}
                value={date}
              />
            </Box>
          </Box>

          <Box
            direction="row"
            align="center"
            justify="between"
            gap="medium"
            flex="grow"
          >
            <SHDText style="subhead-semibold">Time</SHDText>
            <Box width="100px">
              <SHDInputTime
                onChange={(value) => setTime(value)}
                label=""
                minuteStep={5}
                hours={
                  (game && parseInt(game.time.split(':')[0])) ||
                  parseInt(time.value.toString().split(':')[0])
                }
                minutes={
                  (game && parseInt(game.time.split(':')[1])) ||
                  parseInt(time.value.toString().split(':')[1])
                }
              />
            </Box>
          </Box>

          <SHDInput
            label="Opponent team name"
            maxLength={20}
            value={opponent}
            onChange={(val: string) => setOpponent(val)}
          />

          <SHDInput
            label="Game title (Optional)"
            value={gameTitle}
            onChange={(val: string) => setGameTitle(val)}
          />

          <SHDSelect
            label="Sanctioning partner (Optional)"
            options={handleSanctioningPartnerOptions()}
            value={sanctioningPartner}
            onChange={(val: SelectOption) => setSanctioningPartner(val)}
          />

          {mode === 'edit' && (
            <Box margin={{ top: 'small' }} onClick={handleShowDeleteGame}>
              <SHDText style="subhead-semibold" color="dk-red">
                Delete this game
              </SHDText>
            </Box>
          )}
        </Box>
      </SHDModalContainer>
      <SHDFooter>
        <SHDModalContainer>
          <SHDButton
            label={mode === 'create' ? 'Create' : 'Save'}
            primary
            disabled={loading}
            onClick={game ? handleUpdateGame : handleCreateGame}
            busy={loading}
          />
        </SHDModalContainer>
      </SHDFooter>
    </>
  );
};

export default CreateNewGame;
